/* eslint-disable no-console */
import {CloseOutlined, ReloadOutlined} from '@ant-design/icons';
import {Button, Drawer, Flex, Steps, Tag, Tooltip} from 'antd';
import Table, {ColumnsType} from 'antd/es/table';
import moment from 'moment';
import React, {useEffect, useState} from 'react';

import {drawerZIndex} from '../../constants/Common';
import {allUploadType} from '../../services/constants/uploadTypes';
import {jobs} from '../../services/modules/UploadAndJobs/Jobs';
import MySvgComponent from '../DrawerForm/Utils/StepIconDrawer';

interface ModalFormProps {
  open: boolean;
  onCancel: () => void;
  type?: allUploadType;
  data?: any;
  details?: string;
}

interface DataType {
  _id: string;
  key: string;
  jobId: string;
  uploadedBy: string;
  fileName: string;
  subCategory: number;
  description: any;
  status: 'done' | 'in Progress' | 'error';
}

const JobsModal: React.FC<ModalFormProps> = ({
  open,
  onCancel,
  type,
  details
}) => {
  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [jobType] = useState(type);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    if (jobType && open) {
      setIsLoading(true);
      jobs(jobType, details)
        .then((res) => {
          setIsLoading(false);
          setDataSource(res);
          setCurrentPage(1);
        })
        .catch(() => {});
    }
  }, [refresh, open]);

  const status = [
    {value: 'done', label: 'Done'},
    {value: 'in progress', label: 'In progress'},
    {value: 'error', label: 'Rejected/Error'}
  ];

  const RenderTags = (tag: string) => {
    switch (tag.toLocaleLowerCase()) {
      case 'done':
        return 'green';
      case 'in progress':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'green';
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: 'Date',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: '10%',
      render: (value) => {
        return moment(value).format('DD/MM/YYYY');
      }
    },
    {
      title: 'File Name',
      dataIndex: 'fileName',
      key: 'fileName',
      width: '25%',
      className: 'textbreak'
    },
    {
      title: 'Status',
      key: 'tag',
      dataIndex: 'status',
      width: '10%',
      filters: status.map((item) => {
        return {text: item.label, value: item.value};
      }),
      onFilter: (value, record) => {
        return record.status.toLocaleLowerCase() === value;
      },
      render: (_, {status}) => (
        <Tag color={RenderTags(status)} key={status}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Outcome',
      dataIndex: 'description',
      key: 'description',
      className: 'textbreak',
      // width: '30%',
      render: (value) => {
        return (
          <div>
            {typeof value === 'string' ? value ?? '-' : value?.summary ?? '-'}
          </div>
        );
      }
    },
    Table.EXPAND_COLUMN
  ];

  const steps = ['All jobs'];
  const {Step} = Steps;
  const currentStep = 0;

  return (
    <Drawer
      placement={'right'}
      size="large"
      closeIcon={false}
      zIndex={drawerZIndex}
      title={
        <Flex className="stepsBox">
          <Steps current={currentStep}>
            {steps?.map((step, index) => {
              const isFinished = false;
              const isProcess = true;

              return (
                <Step
                  key={step}
                  title={<div>{step}</div>}
                  style={{
                    cursor: 'pointer',
                    textAlign: steps.length === 1 ? 'left' : 'center'
                  }}
                  icon={
                    <div
                      className={
                        isFinished
                          ? 'stepIconCircle isFinished'
                          : isProcess
                            ? 'stepIconCircle isProcess'
                            : 'stepIconCircle'
                      }
                    >
                      <MySvgComponent
                        number={String(index + 1).padStart(2, '0')}
                        isFinished={isFinished}
                        isProcess={isProcess}
                      />
                    </div>
                  }
                />
              );
            })}
          </Steps>
        </Flex>
      }
      open={open}
      onClose={onCancel}
      // cancelButtonProps={{style: {display: 'none'}}}
      style={{background: '#F3F4F8'}}
      footer={
        <Flex className="drawerButtonContainer">
          <div>
            <Button onClick={onCancel} className="drawerButton drawerCancel">
              Cancel
              <CloseOutlined />
            </Button>
          </div>
        </Flex>
      }
      destroyOnClose
    >
      <div className="bodyPage">
        <div style={{height: '100%'}}>
          <Flex vertical>
            <Tooltip title="Refresh Jobs">
              <Button
                type="default"
                icon={<ReloadOutlined />}
                onClick={() => {
                  setRefresh(!refresh);
                }}
                style={{marginBottom: '10px', alignSelf: 'end'}}
              />
            </Tooltip>
            <Table
              columns={columns}
              dataSource={dataSource}
              loading={isLoading}
              pagination={{
                current: currentPage,
                pageSize,
                total: dataSource.length,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (page, size) => {
                  if (size !== pageSize) {
                    setPageSize(size);
                    setCurrentPage(1);
                  } else {
                    setCurrentPage(page);
                  }
                }
              }}
              bordered={false}
              rowKey={'_id'}
              expandable={{
                rowExpandable: (record: any) => {
                  return record?.description?.hasOwnProperty('summary');
                },
                defaultExpandedRowKeys: [],
                // expandedRowKeys: [],
                expandedRowRender: (record) => (
                  <Table
                    key={Math.random()}
                    style={{padding: 0}}
                    pagination={false}
                    rowKey="_id"
                    columns={[
                      {
                        title: 'Successful',
                        dataIndex: 'description',
                        key: 'description',
                        className: 'textbreak',
                        width: '50%',
                        onCell: () => ({
                          style: {verticalAlign: 'top'}
                        }),
                        render: (value) => {
                          return (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-start'
                              }}
                            >
                              {value?.success?.map(
                                (value: string, index: number) => {
                                  return (
                                    <div
                                      key={index}
                                      style={{
                                        display: 'flex',
                                        gap: '10px',
                                        alignItems: 'flex-start',
                                        width: '100%'
                                      }}
                                    >
                                      <div
                                        style={{flex: 'none', minWidth: '24px'}}
                                      >{`${index + 1}.`}</div>
                                      <div style={{flex: 1}}>{value}</div>
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          );
                        }
                      },
                      {
                        title: 'Error',
                        dataIndex: 'description',
                        key: 'description',
                        className: 'textbreak',
                        width: '50%',
                        onCell: () => ({
                          style: {verticalAlign: 'top'}
                        }),
                        render: (value) => {
                          return (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-start'
                              }}
                            >
                              {value?.failed?.map(
                                (value: string, index: number) => {
                                  return (
                                    <div
                                      key={index}
                                      style={{
                                        display: 'flex',
                                        gap: '10px',
                                        alignItems: 'flex-start',
                                        width: '100%'
                                      }}
                                    >
                                      <div
                                        style={{flex: 'none', minWidth: '24px'}}
                                      >{`${index + 1}.`}</div>
                                      <div style={{flex: 1}}>{value}</div>
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          );
                        }
                      }
                    ]}
                    dataSource={[{...record}]}
                    bordered={true}
                  />
                )
              }}
            />
          </Flex>
        </div>
      </div>
    </Drawer>
  );
};

export default JobsModal;
